/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Modal,
  Popper,
  Select,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import SearchCriteria from "models/TransferModels/SearchCriteria";
import SportsBasketballOutlinedIcon from "@mui/icons-material/SportsBasketballOutlined";
import { getImageUrl } from "utils/imageUtils";
import ProfilePreview from "./profile-preview";
import Profil from "models/Profil";
import classes from "components/style";
import searchService, { PublicSearchProfile } from "services/searchService";
import JeSaisOuJeVais from "components/home/je-sais-ou-je-vais";
import { useSelector } from "react-redux";
import { RootState } from "store";
import AdSlot from "components/AdSlot";

type GenderApi = string | undefined;

interface LocationState {
  searchCriteria?: SearchCriteria;
  destinations?: number[];
}

type ProfileCard = {
  key: string;
  mode: "full" | "masked";
  id?: string;
  imagePath: string;
  displayName: string;
  age?: number;
  description?: string;
  stayTypes: string[];
  raw?: Profil;
};

const PAGE_SIZE = 10;

const splitIds = (v?: string | null): number[] => {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => parseInt(s, 10))
    .filter((n) => !Number.isNaN(n));
};

const splitStrings = (v?: string | null): string[] => {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => decodeURIComponent(s).trim())
    .filter((s) => s.length > 0);
};

const parseDate = (v?: string | null): Date | undefined => {
  if (!v) return undefined;
  const d = new Date(decodeURIComponent(v).replace(/\+/g, " "));
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const Profils: FC = () => {
  const navigate = useNavigate();
  const { state, search } = useLocation() as { state?: LocationState; search: string };
  const isAuthenticated = useSelector((s: RootState) => s.globalSlice.isAuthenticated);

  const { stayTypes: allStayTypes, countries: allCountries, durations: allDurations, budgets: allBudgets, transports: allTransports } =
    useSelector((s: RootState) => s.globalSlice);

  const criteriaFromQuery: SearchCriteria | undefined = useMemo(() => {
    const params = new URLSearchParams(search || "");
    const destIds =
      splitIds(params.get("destinationsIds")).length > 0
        ? splitIds(params.get("destinationsIds"))
        : splitIds(params.get("destinations")).length > 0
          ? splitIds(params.get("destinations"))
          : (() => {
            const one = params.get("destinationId");
            return one ? splitIds(one) : [];
          })();

    const stayTypeIds = splitIds(params.get("stayTypes"));
    const durationIds = splitIds(params.get("durations"));
    const budgetIds = splitIds(params.get("budgets"));
    const transportIds = splitIds(params.get("transports"));
    const from = parseDate(params.get("from"));
    const to = parseDate(params.get("to"));
    const continents =
      splitStrings(params.get("continents")).length > 0
        ? splitStrings(params.get("continents"))
        : splitStrings(params.get("continent"));

    const hasAny =
      (destIds.length + stayTypeIds.length + durationIds.length + budgetIds.length + transportIds.length > 0) ||
      (!!from && !!to);
    if (!hasAny) return undefined;

    const mapById = <T extends { id: number }>(ids: number[], pool: T[]) =>
      ids
        .map((id) => pool.find((x) => x.id === id))
        .filter((x): x is T => !!x);

    const destinations = mapById(destIds, allCountries);
    const stayTypes = mapById(stayTypeIds, allStayTypes);
    const durations = mapById(durationIds, allDurations);
    const budgets = mapById(budgetIds, allBudgets);
    const transports = mapById(transportIds, allTransports);

    return {
      from: from ?? (undefined as any),
      to: to ?? (undefined as any),
      stayTypes,
      durations,
      budgets,
      transports,
      destinations,
      continents
    };
  }, [search, allStayTypes, allCountries, allDurations, allBudgets, allTransports]);

  const initialCriteria = state?.searchCriteria ?? criteriaFromQuery;
  const [criteria, setCriteria] = useState<SearchCriteria | undefined>(initialCriteria);

  const [age, setAge] = useState<[number, number]>([18, 100]);
  const [gender, setGender] = useState<GenderApi>(undefined);

  const [anchorAge, setAnchorAge] = useState<null | HTMLElement>(null);
  const [anchorGender, setAnchorGender] = useState<null | HTMLElement>(null);
  const openAge = Boolean(anchorAge);
  const openGender = Boolean(anchorGender);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cards, setCards] = useState<ProfileCard[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  useEffect(() => {
    if (state?.searchCriteria) return;
    if (!criteriaFromQuery) return;
    setCriteria(criteriaFromQuery);
  }, [criteriaFromQuery, state?.searchCriteria]);

  const canQuery = useMemo(() => !!criteria?.from && !!criteria?.to, [criteria]);

  const loadProfiles = useCallback(async (page = 1, append = false) => {
    if (!criteria || !canQuery) return;

    if (page === 1) {
      setIsLoading(true);
      setError(null);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const request = {
        from: criteria.from,
        to: criteria.to,
        stayTypes: criteria.stayTypes.map((s) => s.id),
        destinations: criteria.destinations.map((s) => s.id),
        minAge: age[0],
        maxAge: age[1],
        gender,
        continents: criteria.continents
      };

      if (isAuthenticated) {
        const fullProfiles = await searchService.search(request, page, PAGE_SIZE);
        const mappedCards: ProfileCard[] = (fullProfiles ?? []).map((p) => ({
          key: p?.user?.id ?? `${p.country?.id}-${p.from}-${page}`,
          mode: "full",
          id: p?.user?.id,
          imagePath: p?.user?.profilePicture ?? "default_avatar",
          displayName: `${p?.user?.firstName ?? ""} ${p?.user?.lastName ?? ""}`.trim(),
          age: p?.user?.age,
          description: p?.user?.description,
          stayTypes: (p?.stayTypes ?? []).map((s) => s.name),
          raw: p
        }));

        setCards((prev) => (append ? [...prev, ...mappedCards] : mappedCards));
        setHasMore(mappedCards.length === PAGE_SIZE);
      } else {
        const maskedProfiles = await searchService.searchPublic(request, page, PAGE_SIZE);
        const mappedCards: ProfileCard[] = (maskedProfiles ?? []).map((p: PublicSearchProfile, index: number) => ({
          key: `${p?.country?.id}-${p?.from}-${page}-${index}`,
          mode: "masked",
          imagePath: p.blurredProfilePicture,
          displayName: p?.firstName ?? "·",
          age: p?.age,
          stayTypes: (p?.stayTypes ?? []).map((s) => s.name)
        }));

        setCards((prev) => (append ? [...prev, ...mappedCards] : mappedCards));
        setHasMore(mappedCards.length === PAGE_SIZE);
      }

      setCurrentPage(page);
    } catch (e: any) {
      if (page === 1) {
        setError(e?.message ?? "Impossible de charger les profils");
      }
    } finally {
      if (page === 1) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  }, [criteria, canQuery, age, gender, isAuthenticated]);

  useEffect(() => {
    loadProfiles(1, false);
  }, [loadProfiles]);

  const loadMoreProfiles = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    loadProfiles(currentPage + 1, true);
  }, [isLoading, isLoadingMore, hasMore, loadProfiles, currentPage]);

  const handleChangeAge = (_: Event, newValue: number | number[], activeThumb: number) => {
    if (!Array.isArray(newValue)) return;
    const minDistance = 1;
    if (activeThumb === 0) setAge([Math.min(newValue[0], age[1] - minDistance), age[1]]);
    else setAge([age[0], Math.max(newValue[1], age[0] + minDistance)]);
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ width: "100%", position: "relative", my: 2 }}>
        <Card sx={[classes.card, classes.fullWidth, classes.calendrier]}>
          {criteria && <JeSaisOuJeVais searchCriteria={criteria} />}
        </Card>
      </Box>

      <AdSlot zone='PROFILS_TOP' />

      {!isAuthenticated && cards.length > 0 && (
        <Card sx={[classes.card, { mb: 3 }]}>
          <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography fontWeight={600}>
              Si vous voulez voir ces profils, vous devez vous inscrire.
            </Typography>
            <Button variant="contained" onClick={() => navigate(`/auth${search ?? ""}`)}>
              S'inscrire
            </Button>
          </CardContent>
        </Card>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Profils</Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={(e) => setAnchorAge(anchorAge ? null : e.currentTarget)} sx={classes.filterBtn}>
            Age
          </Button>
          <Button variant="text" onClick={(e) => setAnchorGender(anchorGender ? null : e.currentTarget)} sx={classes.filterBtn}>
            Sexe
          </Button>
        </Stack>
      </Stack>

      <Popper id="popper_age" open={openAge} anchorEl={anchorAge}>
        <Box sx={{ width: 240, p: 2, border: 1, bgcolor: "background.paper", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Slider sx={{ width: "90%" }} value={age} onChange={handleChangeAge} min={18} max={100} valueLabelDisplay="auto" disableSwap />
          <Button onClick={() => { setAnchorAge(null); loadProfiles(1, false); }}>OK</Button>
        </Box>
      </Popper>

      <Popper id="popper_gender" open={openGender} anchorEl={anchorGender}>
        <Box sx={{ width: 260, p: 2, border: 1, bgcolor: "background.paper" }}>
          <FormControl fullWidth size="small">
            <InputLabel id="gender-label">Sexe</InputLabel>
            <Select
              labelId="gender-label"
              label="Sexe"
              value={gender ?? ""}
              onChange={(e) => setGender((e.target.value || undefined) as GenderApi)}
            >
              <MenuItem value="">Indifferent</MenuItem>
              <MenuItem value={"Male"}>Homme</MenuItem>
              <MenuItem value={"Female"}>Femme</MenuItem>
            </Select>
          </FormControl>
          <Stack alignItems="flex-end" mt={1}>
            <Button onClick={() => { setAnchorGender(null); loadProfiles(1, false); }}>OK</Button>
          </Stack>
        </Box>
      </Popper>

      {isLoading && (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" mt={2}>Chargement des profils...</Typography>
        </Stack>
      )}

      {error && (
        <Card sx={[classes.card, { mb: 2 }]}>
          <CardContent>
            <Typography color="error">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Grid container spacing={3} sx={{ mt: 1, mb: 1 }}>
            {cards.length === 0 && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={5}>
                  <Typography textAlign="center" fontWeight={500} fontSize={20}>Aucun profil</Typography>
                </Box>
              </Grid>
            )}

            {cards.map((card) => (
              <Grid
                item
                xs={12}
                key={card.key}
                onClick={() => card.mode === "full" && card.id && setSelectedProfileId(card.id)}
              >
                <Card>
                  <Grid container alignItems="center" direction="row" justifyContent="center">
                    <Grid item xs={5} md={3} sm={3} lg={2} sx={{ justifyContent: "center", alignItems: "center", p: 1 }}>
                      <CardMedia
                        component="img"
                        image={getImageUrl(card.imagePath)}
                        alt={card.displayName}
                        sx={{ padding: "1em 1em 0 1em", objectFit: "contain" }}
                      />
                    </Grid>

                    <Grid item xs={7} sm={8} md={8} lg={2} sx={{ justifyContent: "center", alignItems: "center", p: 2, m: 1 }}>
                      <Typography component="div" variant="h5">{card.displayName}</Typography>
                      {card.age ? (
                        <Typography component="div" variant="body1">{card.age} ans</Typography>
                      ) : null}
                    </Grid>

                    <Grid item sm={12} md={6} lg={3} sx={{ justifyContent: "center", alignItems: "center", display: { lg: "block" }, p: 2 }}>
                      <Typography component="div" variant="body2">
                        {card.mode === "masked" ? "Profil anonymise. Inscrivez-vous pour voir plus de details." : (card.description ?? "")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} lg={2} sx={{ justifyContent: "center", alignItems: "center", p: 2 }}>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar>
                              <SportsBasketballOutlinedIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={(card.stayTypes || []).join(", ")} />
                        </ListItem>
                      </List>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} lg={2} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {card.mode === "full" ? (
                        <Button variant="contained">Inviter</Button>
                      ) : (
                        <Button variant="outlined" onClick={() => navigate(`/auth${search ?? ""}`)}>
                          Voir le profil
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>

          {hasMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button variant="outlined" onClick={loadMoreProfiles} disabled={isLoadingMore}>
                {isLoadingMore ? "Chargement..." : "Voir plus"}
              </Button>
            </Box>
          )}
        </>
      )}

      <AdSlot zone='PROFILS_BOT' />

      {isAuthenticated && cards.length > 0 && (
        <Modal
          open={!!selectedProfileId}
          onClose={() => setSelectedProfileId(null)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <ProfilePreview
            open={!!selectedProfileId}
            handleClose={() => setSelectedProfileId(null)}
            profile={cards.find((p) => p.mode === "full" && p.id === selectedProfileId)?.raw}
          />
        </Modal>
      )}
    </Container>
  );
};

export default Profils;
